import { useState, useCallback } from 'react';
import { Title } from 'react-admin';
import { useQuery, useMutation } from '@apollo/client';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TablePagination from '@mui/material/TablePagination';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import TextField from '@mui/material/TextField';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import InputAdornment from '@mui/material/InputAdornment';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Skeleton from '@mui/material/Skeleton';
import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import Grid from '@mui/material/Grid';
import Divider from '@mui/material/Divider';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import VisibilityIcon from '@mui/icons-material/Visibility';
import UndoIcon from '@mui/icons-material/Undo';
import FilterListIcon from '@mui/icons-material/FilterList';
import ClearIcon from '@mui/icons-material/Clear';

import { GET_ADMIN_TRANSACTIONS, GET_TRANSACTION_DETAILS, ADMIN_REFUND_PAYMENT } from './payment.queries';
import {
  Transaction,
  TransactionFilter,
  PaymentStatus,
  PaymentProvider,
  PaymentType,
  formatAmount,
  formatDate,
  PROVIDER_LABELS,
  PROVIDER_COLORS,
  STATUS_LABELS,
  STATUS_COLORS,
  METHOD_LABELS,
  TYPE_LABELS,
} from './payment.types';
import { CURRENCY_LABEL } from '@ekaa/tokens/vocabulary';

/**
 * Status badge component
 */
function StatusBadge({ status }: { status: PaymentStatus }) {
  return (
    <Chip
      label={STATUS_LABELS[status]}
      size="small"
      sx={{
        backgroundColor: `${STATUS_COLORS[status]}20`,
        color: STATUS_COLORS[status],
        fontWeight: 500,
      }}
    />
  );
}

/**
 * Provider badge component
 */
function ProviderBadge({ provider }: { provider: PaymentProvider }) {
  return (
    <Chip
      label={PROVIDER_LABELS[provider]}
      size="small"
      variant="outlined"
      sx={{
        borderColor: PROVIDER_COLORS[provider],
        color: PROVIDER_COLORS[provider],
      }}
    />
  );
}

/**
 * Transaction details dialog
 */
interface TransactionDialogProps {
  transactionId: string | null;
  open: boolean;
  onClose: () => void;
  onRefund: (id: string) => void;
}

function TransactionDetailsDialog({ transactionId, open, onClose, onRefund }: TransactionDialogProps) {
  const { data, loading } = useQuery<{ transaction: Transaction }>(GET_TRANSACTION_DETAILS, {
    variables: { id: transactionId },
    skip: !transactionId,
  });

  const transaction = data?.transaction;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Détails de la transaction
        {transaction && (
          <Typography variant="body2" color="text.secondary">
            Référence: {transaction.orderId}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box sx={{ p: 2 }}>
            <Skeleton variant="text" height={40} />
            <Skeleton variant="text" height={40} />
            <Skeleton variant="rectangular" height={200} sx={{ mt: 2 }} />
          </Box>
        ) : transaction ? (
          <Grid container spacing={3}>
            {/* Main info */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Informations générales
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">Montant</Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {formatAmount(transaction.amount, transaction.currency)}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">Statut</Typography>
                  <StatusBadge status={transaction.status} />
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">Provider</Typography>
                  <ProviderBadge provider={transaction.provider} />
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">Méthode</Typography>
                  <Typography variant="body2">{METHOD_LABELS[transaction.paymentMethod]}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">Type</Typography>
                  <Typography variant="body2">{TYPE_LABELS[transaction.type]}</Typography>
                </Box>
                {transaction.walletAmount && transaction.walletAmount > 0 && (
                  <>
                    <Divider />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2">Montant {CURRENCY_LABEL}</Typography>
                      <Typography variant="body2" color="primary">
                        {formatAmount(transaction.walletAmount, 'EKAA')}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2">Montant Cash</Typography>
                      <Typography variant="body2">
                        {formatAmount(transaction.cashAmount || 0, transaction.currency)}
                      </Typography>
                    </Box>
                  </>
                )}
              </Box>
            </Grid>

            {/* User info */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Utilisateur
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">Nom</Typography>
                  <Typography variant="body2">{transaction.user?.profile?.displayName || '-'}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">Email</Typography>
                  <Typography variant="body2">{transaction.user?.email || '-'}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">Téléphone</Typography>
                  <Typography variant="body2">{transaction.user?.profile?.phoneNumber || '-'}</Typography>
                </Box>
              </Box>

              <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 3 }} gutterBottom>
                Dates
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">Création</Typography>
                  <Typography variant="body2">{formatDate(transaction.createdAt)}</Typography>
                </Box>
                {transaction.confirmedAt && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2">Confirmation</Typography>
                    <Typography variant="body2">{formatDate(transaction.confirmedAt)}</Typography>
                  </Box>
                )}
              </Box>
            </Grid>

            {/* Error message */}
            {transaction.errorMessage && (
              <Grid item xs={12}>
                <Alert severity="error">
                  <Typography variant="body2">{transaction.errorMessage}</Typography>
                </Alert>
              </Grid>
            )}

            {/* Webhook history */}
            {transaction.webhookHistory && transaction.webhookHistory.length > 0 && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Historique des webhooks
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Événement</TableCell>
                        <TableCell>Date</TableCell>
                        <TableCell>Statut</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {transaction.webhookHistory.map((webhook) => (
                        <TableRow key={webhook.id}>
                          <TableCell>{webhook.event}</TableCell>
                          <TableCell>{formatDate(webhook.receivedAt)}</TableCell>
                          <TableCell>
                            <Chip
                              label={webhook.processed ? 'Traité' : 'Erreur'}
                              size="small"
                              color={webhook.processed ? 'success' : 'error'}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
            )}

            {/* Refunds */}
            {transaction.refunds && transaction.refunds.length > 0 && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Remboursements
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Montant</TableCell>
                        <TableCell>Raison</TableCell>
                        <TableCell>Statut</TableCell>
                        <TableCell>Date</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {transaction.refunds.map((refund) => (
                        <TableRow key={refund.id}>
                          <TableCell>{formatAmount(refund.amount, transaction.currency)}</TableCell>
                          <TableCell>{refund.reason || '-'}</TableCell>
                          <TableCell>
                            <Chip
                              label={refund.status}
                              size="small"
                              color={refund.status === 'COMPLETED' ? 'success' : 'warning'}
                            />
                          </TableCell>
                          <TableCell>{formatDate(refund.createdAt)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
            )}
          </Grid>
        ) : (
          <Typography color="text.secondary">Transaction non trouvée</Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Fermer</Button>
        {transaction && transaction.status === 'COMPLETED' && !transaction.refunds?.length && (
          <Button
            onClick={() => onRefund(transaction.id)}
            color="warning"
            startIcon={<UndoIcon />}
          >
            Rembourser
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

/**
 * Refund dialog
 */
interface RefundDialogProps {
  transactionId: string | null;
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

function RefundDialog({ transactionId, open, onClose, onSuccess }: RefundDialogProps) {
  const [reason, setReason] = useState('');
  const [amount, setAmount] = useState<number | ''>('');

  const [refundPayment, { loading }] = useMutation(ADMIN_REFUND_PAYMENT, {
    onCompleted: (data) => {
      if (data.adminRefundPayment.success) {
        onSuccess();
        onClose();
        setReason('');
        setAmount('');
      }
    },
  });

  const handleRefund = () => {
    if (!transactionId || !reason) return;
    refundPayment({
      variables: {
        paymentId: transactionId,
        amount: amount || undefined,
        reason,
      },
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Rembourser la transaction</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="Montant (optionnel, laissez vide pour remboursement total)"
            type="number"
            value={amount}
            onChange={(e) => setAmount(e.target.value ? Number(e.target.value) : '')}
            fullWidth
            InputProps={{
              endAdornment: <InputAdornment position="end">FCFA</InputAdornment>,
            }}
          />
          <TextField
            label="Raison du remboursement"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            fullWidth
            multiline
            rows={3}
            required
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Annuler</Button>
        <Button
          onClick={handleRefund}
          variant="contained"
          color="warning"
          disabled={!reason || loading}
        >
          {loading ? 'En cours...' : 'Rembourser'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * Transaction List Component
 */
export function TransactionList() {
  // Pagination state
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);

  // Filter state
  const [filters, setFilters] = useState<TransactionFilter>({});
  const [showFilters, setShowFilters] = useState(false);
  const [search, setSearch] = useState('');

  // Dialog state
  const [selectedTransaction, setSelectedTransaction] = useState<string | null>(null);
  const [refundTransaction, setRefundTransaction] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // Query
  const { data, loading, error, refetch } = useQuery<{
    adminTransactions: { data: Transaction[]; total: number };
  }>(GET_ADMIN_TRANSACTIONS, {
    variables: {
      filter: { ...filters, search: search || undefined },
      sort: { field: 'createdAt', order: 'DESC' },
      pagination: { page: page + 1, pageSize },
    },
  });

  const transactions = data?.adminTransactions.data || [];
  const total = data?.adminTransactions.total || 0;

  // Handlers
  const handleChangePage = useCallback((_: unknown, newPage: number) => {
    setPage(newPage);
  }, []);

  const handleChangeRowsPerPage = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setPageSize(parseInt(event.target.value, 10));
    setPage(0);
  }, []);

  const handleFilterChange = useCallback((key: keyof TransactionFilter, value: string | undefined) => {
    setFilters((prev) => ({ ...prev, [key]: value || undefined }));
    setPage(0);
  }, []);

  const clearFilters = useCallback(() => {
    setFilters({});
    setSearch('');
    setPage(0);
  }, []);

  const handleRefundSuccess = useCallback(() => {
    setSnackbar({ open: true, message: 'Remboursement initié avec succès', severity: 'success' });
    refetch();
  }, [refetch]);

  return (
    <Box>
      <Title title="Transactions" />

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight={600}>
          Liste des transactions
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Filtres">
            <IconButton onClick={() => setShowFilters(!showFilters)}>
              <FilterListIcon color={Object.keys(filters).length > 0 ? 'primary' : 'inherit'} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Actualiser">
            <IconButton onClick={() => refetch()}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Search and filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <TextField
              placeholder="Rechercher par référence, email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              size="small"
              sx={{ minWidth: 300 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />

            {showFilters && (
              <>
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel>Statut</InputLabel>
                  <Select
                    value={filters.status || ''}
                    label="Statut"
                    onChange={(e) => handleFilterChange('status', e.target.value as PaymentStatus)}
                  >
                    <MenuItem value="">Tous</MenuItem>
                    {Object.entries(STATUS_LABELS).map(([key, label]) => (
                      <MenuItem key={key} value={key}>
                        {label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel>Provider</InputLabel>
                  <Select
                    value={filters.provider || ''}
                    label="Provider"
                    onChange={(e) => handleFilterChange('provider', e.target.value as PaymentProvider)}
                  >
                    <MenuItem value="">Tous</MenuItem>
                    {Object.entries(PROVIDER_LABELS).map(([key, label]) => (
                      <MenuItem key={key} value={key}>
                        {label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel>Type</InputLabel>
                  <Select
                    value={filters.type || ''}
                    label="Type"
                    onChange={(e) => handleFilterChange('type', e.target.value as PaymentType)}
                  >
                    <MenuItem value="">Tous</MenuItem>
                    {Object.entries(TYPE_LABELS).map(([key, label]) => (
                      <MenuItem key={key} value={key}>
                        {label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Button
                  variant="outlined"
                  startIcon={<ClearIcon />}
                  onClick={clearFilters}
                  size="small"
                >
                  Effacer
                </Button>
              </>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Error */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Erreur lors du chargement des transactions
        </Alert>
      )}

      {/* Table */}
      <Card>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Référence</TableCell>
                <TableCell>Utilisateur</TableCell>
                <TableCell align="right">Montant</TableCell>
                <TableCell>Statut</TableCell>
                <TableCell>Provider</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Date</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                Array.from({ length: 5 }).map((_, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <Skeleton variant="text" />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant="text" />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant="text" />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant="text" width={80} />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant="text" width={80} />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant="text" />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant="text" />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant="circular" width={32} height={32} />
                    </TableCell>
                  </TableRow>
                ))
              ) : transactions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">Aucune transaction trouvée</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                transactions.map((transaction) => (
                  <TableRow key={transaction.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {transaction.orderId}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {transaction.user?.profile?.displayName || transaction.user?.email || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight={600}>
                        {formatAmount(transaction.amount, transaction.currency)}
                      </Typography>
                      {transaction.walletAmount && transaction.walletAmount > 0 && (
                        <Typography variant="caption" color="primary" display="block">
                          dont {formatAmount(transaction.walletAmount, 'EKAA')}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={transaction.status} />
                    </TableCell>
                    <TableCell>
                      <ProviderBadge provider={transaction.provider} />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{TYPE_LABELS[transaction.type]}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{formatDate(transaction.createdAt)}</Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Voir les détails">
                        <IconButton
                          size="small"
                          onClick={() => setSelectedTransaction(transaction.id)}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={pageSize}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 25, 50, 100]}
          labelRowsPerPage="Par page:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} sur ${count}`}
        />
      </Card>

      {/* Transaction details dialog */}
      <TransactionDetailsDialog
        transactionId={selectedTransaction}
        open={Boolean(selectedTransaction)}
        onClose={() => setSelectedTransaction(null)}
        onRefund={(id) => {
          setSelectedTransaction(null);
          setRefundTransaction(id);
        }}
      />

      {/* Refund dialog */}
      <RefundDialog
        transactionId={refundTransaction}
        open={Boolean(refundTransaction)}
        onClose={() => setRefundTransaction(null)}
        onSuccess={handleRefundSuccess}
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default TransactionList;
